import { supabase } from './supabase';

export interface CustomerSource {
  id: string;
  business_id: string;
  name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateCustomerSourceParams {
  business_id: string;
  name: string;
}

export interface UpdateCustomerSourceParams {
  name?: string;
  is_active?: boolean;
}

/**
 * Customer Sources Service
 * 
 * Handles customer acquisition source management for businesses.
 * Allows tracking where customers come from (Google Ads, Referrals, etc.)
 */
export class CustomerSourcesService {
  
  /**
   * Get all active customer sources for a business
   */
  static async getCustomerSources(businessId: string): Promise<CustomerSource[]> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const { data, error } = await supabase
        .from('customer_sources')
        .select('*')
        .eq('business_id', businessId)
        .eq('is_active', true)
        .order('name');

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching customer sources:', error);
      throw error;
    }
  }

  /**
   * Create a new customer source
   */
  static async createCustomerSource(params: CreateCustomerSourceParams): Promise<CustomerSource> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      // Trim and validate the name
      const trimmedName = params.name.trim();
      if (!trimmedName) {
        throw new Error('Customer source name cannot be empty');
      }

      const { data, error } = await supabase
        .from('customer_sources')
        .insert({
          business_id: params.business_id,
          name: trimmedName,
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          throw new Error('A customer source with this name already exists');
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error creating customer source:', error);
      throw error;
    }
  }

  /**
   * Update an existing customer source
   */
  static async updateCustomerSource(
    sourceId: string, 
    params: UpdateCustomerSourceParams
  ): Promise<CustomerSource> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const updateData: any = {
        updated_at: new Date().toISOString()
      };

      if (params.name !== undefined) {
        const trimmedName = params.name.trim();
        if (!trimmedName) {
          throw new Error('Customer source name cannot be empty');
        }
        updateData.name = trimmedName;
      }

      if (params.is_active !== undefined) {
        updateData.is_active = params.is_active;
      }

      const { data, error } = await supabase
        .from('customer_sources')
        .update(updateData)
        .eq('id', sourceId)
        .select()
        .single();

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          throw new Error('A customer source with this name already exists');
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error updating customer source:', error);
      throw error;
    }
  }

  /**
   * Deactivate a customer source (soft delete)
   */
  static async deactivateCustomerSource(sourceId: string): Promise<void> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const { error } = await supabase
        .from('customer_sources')
        .update({
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', sourceId);

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error deactivating customer source:', error);
      throw error;
    }
  }

  /**
   * Get a specific customer source by ID
   */
  static async getCustomerSource(sourceId: string): Promise<CustomerSource | null> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const { data, error } = await supabase
        .from('customer_sources')
        .select('*')
        .eq('id', sourceId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // No data found
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching customer source:', error);
      throw error;
    }
  }
} 